import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useState } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PURGE } from 'redux-persist';
import tw from 'twin.macro';
import * as yup from 'yup';

import { useMyDocumentsAuthMutation, useMyDocumentsSendOtpMutation } from '@/app/api/my-documents';
import { setAuth } from '@/app/store/slices/auth';
import { getChannel } from '@/app/store/slices/channel';
import TickIcon from '@/assets/images/icons/TickIcon';
import { BigTitle, BodyText, SubBody, SubTitle, Title } from '@/components';
import { ThemeContext } from '@/contexts/theme-context';
import LoginContent from '@/views/login';

function MyDocumentsAuth() {
  const { theme, setTheme } = useContext(ThemeContext);
  const [countryFormatLength, setCountryFormatLength] = useState(11);
  const channel = useSelector(getChannel);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isExeceptionCountry, setIsExeceptionCountry] = useState(false);
  const { t } = useTranslation();
  const schema = yup
    .object({
      mobile_phone: yup
        .string()
        .trim()
        .test('len', '', val => {
          if (isExeceptionCountry) {
            if (val.length !== countryFormatLength && val.length !== countryFormatLength + 1) {
              return false;
            } else {
              return true;
            }
          } else {
            return String(val).length === countryFormatLength && !(String(val).length > countryFormatLength);
          }
        })
        .required()
    })
    .required();

  const {
    control,
    setValue,
    watch,
    resetField,
    getValues,
    setError,
    formState: { isValid, errors }
  } = useForm({ mode: 'onChange', resolver: yupResolver(schema) });

  const mobile_phone = watch('mobile_phone');
  const code = watch('code');
  const [fetchSendOtp, { isSuccess: sendOtpSuccess, error: sendOtpError, isLoading: sendOtpLoading }] =
    useMyDocumentsSendOtpMutation();
  const [fetchApplyAuth, { isSuccess: applyAuthSuccess, error: applyAuthError, data, isLoading: applyAuthLoading }] =
    useMyDocumentsAuthMutation();

  const sendOtp = useCallback(() => {
    let phone = '';
    if (!mobile_phone?.includes('+')) {
      phone = '+' + mobile_phone?.replaceAll(' ', '');
    } else {
      phone = mobile_phone?.replaceAll(' ', '');
    }

    if (phone?.length >= 12 && !sendOtpSuccess) {
      fetchSendOtp({ mobile_phone: phone });
    }
  }, [mobile_phone, applyAuthSuccess, fetchSendOtp]);

  const handleResendOtp = useCallback(() => {
    const phone = '+' + mobile_phone?.replaceAll(' ', '');
    fetchSendOtp({ mobile_phone: phone });
  }, [mobile_phone, fetchSendOtp]);

  useEffect(() => {
    if (sendOtpError?.data?.error?.code === 'INVALID_OTP') {
      setError('code', { type: 'value', message: sendOtpError?.data?.error?.description });
    }
    if (sendOtpError?.data?.error?.code === 'NO_ACTIVE_ACCOUNT') {
      setError('mobile_phone', { type: 'value', message: sendOtpError?.data?.error?.description });
    }
    if (sendOtpError?.data?.error?.code === 'NO_ACTIVE_CARD') {
      setError('mobile_phone', { type: 'value', message: sendOtpError?.data?.error?.description });
    }
  }, [sendOtpError, setError]);

  useEffect(() => {
    if (code?.length === 4) {
      if (mobile_phone) {
        const withPlus = '+' + mobile_phone;
        document.cookie = `channel=${channel}; max-age=300`;
        fetchApplyAuth({ code, mobile_phone: withPlus });
      }
    }
  }, [code, mobile_phone, fetchApplyAuth]);

  useEffect(() => {
    dispatch({ type: PURGE, result: () => null });
  }, [dispatch]);

  useEffect(() => {
    if (applyAuthError) {
      setError('code', { type: 'value', message: applyAuthError?.data?.error?.description });
    }
  }, [applyAuthError, setError]);

  useEffect(() => {
    if (applyAuthSuccess) {
      dispatch(setAuth(data?.data));
      navigate('/my-documents-main');
    }
  }, [applyAuthSuccess]);

  const translate = line => {
    return t(`my_documents_auth.${line}`);
  };
  const benefits = useMemo(
    () => [
      translate('functionality.first'),
      translate('functionality.second'),

      {
        text: translate('functionality.third.title'),
        stickerText: translate('functionality.third.text')
      },
      translate('functionality.fourth'),
      translate('functionality.fifth')
    ],
    []
  );

  useEffect(() => {
    setTheme('dark');
  }, [setTheme]);

  return (
    <div>
      <div tw='flex flex-col gap-5'>
        <BigTitle text={translate('title')} twStyle={tw`sm:text-[50px] text-[32px] text-[#70FF6E]`} />
        <SubTitle text={translate('caption')} />
        <div tw='flex sm:flex-row flex-col gap-5 w-full mt-5'>
          <div tw='bg-[#444444] p-5 rounded-2xl '>
            <Title variant={'bold'} text={translate('functionality.title')} />
            <div tw='sm:py-5 flex flex-col lg:row-gap-5 row-gap-5 justify-between lg:mt-0 mt-5 '>
              {/* <div tw='flex gap-2 items-start'>
                <TickIcon />
                <BodyText text={'Заказ пластиковой карты с доставкой (в дополнение к цифровой)'} />
              </div> */}
              {benefits?.map(item =>
                typeof item !== 'object' ? (
                  <div tw='flex gap-2 items-start' key={item}>
                    <TickIcon />
                    <BodyText text={item} />
                  </div>
                ) : (
                  <div tw='flex gap-2 items-start ' key={item}>
                    <TickIcon />
                    <div tw='flex flex-col gap-3 -mt-1'>
                      <BodyText
                        text={
                          <span>
                            {item?.text}
                            <span tw='bg-[rgba(147, 147, 147, 0.2)] rounded-xl px-2 h-[25px] ml-2 mt-2 text-s12 sm:inline inline-block max-w-max py-1'>
                              {item?.stickerText}
                            </span>
                          </span>
                        }
                        twStyle={tw`leading-7`}
                      />
                      <SubBody text={item?.capton} twStyle={tw`text-secondary`} />
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
          <div tw='bg-[#444444] p-5 rounded-2xl sm:w-1/2 flex flex-col gap-2'>
            <Title variant={'bold'} text={translate('auth.title')} />
            <LoginContent
              phone={mobile_phone}
              control={control}
              authSuccess={sendOtpSuccess}
              verifySubmit={sendOtp}
              setIsExeceptionCountry={setIsExeceptionCountry}
              recaptchaValue={''}
              mobilePhoneValid={isValid}
              isExeceptionCountry={isExeceptionCountry}
              loading={sendOtpLoading || applyAuthLoading}
              reset={resetField}
              authReset={handleResendOtp}
              sendOtpError={sendOtpError}
              errors={errors}
              setValue={setValue}
              setCountryFormatLength={setCountryFormatLength}
              getValues={getValues}
              twStyle={tw`px-0 bg-[#444444] mt-0 py-2`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyDocumentsAuth;
